import { Route, Routes, useParams } from 'react-router-dom';

import { NotFound } from '@/components/molecules/NotFound';
import { useAuth } from '@/contexts/auth';
import UserManagementView from '@/routes/shared/UserManagementPage';
import { parsePositiveInt } from '@/utils/parsePositiveInt';

import { CageRoutes } from '../Cage/CageRoutes';
import HelpPageContainer from '../components/Help/HelpPageContainer';
import { MoveBioscopeContainer } from '../MoveBioScope/MoveBioscopeContainer';

import DataSources from './pages/DataSources';
import { FeatureFlagRoute } from './pages/FeatureFlagRoute';
import FeedingDashboard from './pages/FeedingDashboard';
import FishGroupBoard from './pages/FishGroupBoard';
import LocationCompare from './pages/LocationCompare';
import LocationDashboard from './pages/LocationDashboard';
import LocationLiceReportContainer from './pages/LocationLiceReport/LocationLiceReportContainer';
import LocationVideoContainer from './pages/LocationVideo/LocationVideoContainer';
import OperationalDashboard from './pages/OperationalDashboard';
import { OptoScaleAdminRoute } from './pages/OptoScaleAdminRoute';
import { ProtectedRoute } from './pages/ProtectedRoute';

interface LocationRoutesProps {
    clientId: number;
    /** The region URL (`/c/:clientId`), where a disabled feature page links back to. */
    regionUrl: string;
}

/**
 * Every page under `/c/:clientId/location/:locationId`, pens included. A site the client does
 * not have (an unknown id, or another client's) is not found rather than an empty dashboard.
 */
export function LocationRoutes(props: LocationRoutesProps) {
    const { clientId, regionUrl } = props;
    const { locationId: locationParam } = useParams();
    const { clients } = useAuth();
    const locationId = parsePositiveInt(locationParam);
    const client = clients.find((candidate) => candidate.id === clientId);
    const isClientSite = client?.locations.some((location) => location.id === locationId);

    if (locationId === null || !isClientSite) {
        return <NotFound />;
    }

    const ids = { clientId, locationId };

    return (
        <Routes>
            <Route
                path="pen/:cageId/*"
                element={<CageRoutes clientId={clientId} locationId={locationId} />}
            />
            <Route path="help" element={<HelpPageContainer />} />
            <Route path="lice-report" element={<LocationLiceReportContainer {...ids} />} />
            <Route path="users" element={<UserManagementView clientId={clientId} />} />
            <Route path="change-pen" element={<MoveBioscopeContainer {...ids} />} />
            <Route path="compare" element={<LocationCompare {...ids} />} />
            <Route
                path="video"
                element={
                    <ProtectedRoute requiredLicenses={['^LIVE_VIDEO_STREAM$']}>
                        <LocationVideoContainer {...ids} />
                    </ProtectedRoute>
                }
            />
            <Route
                path="feeding"
                element={
                    <FeatureFlagRoute
                        flag="feeding-dashboard"
                        unavailableLinkTo={`${regionUrl}/location/${locationParam}`}>
                        <FeedingDashboard
                            // Remount per location so pen selection / date range
                            // don't carry over when navigating between sites in place.
                            key={locationParam}
                            {...ids}
                        />
                    </FeatureFlagRoute>
                }
            />
            <Route
                path="fish-groups"
                element={
                    <FeatureFlagRoute
                        flag="fish-groups-client-management"
                        unavailableLinkTo={`${regionUrl}/location/${locationParam}`}>
                        <FishGroupBoard key={locationParam} {...ids} />
                    </FeatureFlagRoute>
                }
            />
            <Route
                path="data-sources"
                element={
                    // OptoScale admins only for now, even where the flag is on.
                    <OptoScaleAdminRoute
                        unavailableLinkTo={`${regionUrl}/location/${locationParam}`}>
                        <FeatureFlagRoute
                            flag="provider-config-ui"
                            unavailableLinkTo={`${regionUrl}/location/${locationParam}`}>
                            <DataSources
                                // Remount per location so one site's unsaved edits never
                                // show on the next site's page.
                                key={locationParam}
                                {...ids}
                            />
                        </FeatureFlagRoute>
                    </OptoScaleAdminRoute>
                }
            />
            <Route path="operational-dashboard" element={<OperationalDashboard {...ids} />} />
            <Route index element={<LocationDashboard {...ids} />} />

            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}
